import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  RocketLaunch as RocketLaunchIcon,
  Visibility,
  VisibilityOff,
} from '../../components/icons';
import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
  Avatar,
  Card,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import PhoneNumberInput from '../../components/common/PhoneNumberInput';
import { parsePhoneNumber } from 'react-phone-number-input';
import { getErrorMessage } from '../../utils/errorUtils';
import { BRAND, SUCCESS, NEUTRAL } from '../../theme/adminColors';

// Styled TextField with modern design
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#f8fafc',
    border: '2px solid transparent',
    transition: 'all 0.3s ease',
    '& fieldset': {
      border: '2px solid #e2e8f0',
      transition: 'all 0.3s ease',
    },
    '&:hover': {
      backgroundColor: '#f1f5f9',
      '& fieldset': {
        borderColor: '#cbd5e1',
      },
    },
    '&.Mui-focused': {
      backgroundColor: 'white',
      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
      '& fieldset': {
        borderColor: BRAND.main,
        borderWidth: '2px',
      },
    },
    '&.Mui-disabled': {
      backgroundColor: '#f1f5f9',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#64748b',
    fontWeight: 500,
    '&.Mui-focused': {
      color: BRAND.main,
      fontWeight: 600,
    },
  },
  '& .MuiFormHelperText-root': {
    marginLeft: '4px',
    fontSize: '0.875rem',
  },
}));

const UserRegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    phone: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (!formData.password) {
      setError('Password is required');
      return false;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (!formData.fullName.trim()) {
      setError('Full name is required');
      return false;
    }

    // Validate phone number if provided
    if (formData.phone) {
      try {
        const phoneNumber = parsePhoneNumber(formData.phone);
        if (!phoneNumber || !phoneNumber.isValid()) {
          setError('Please enter a valid phone number');
          return false;
        }
      } catch (error) {
        setError('Please enter a valid phone number');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);

      // Parse phone number to extract country code and national number
      let phoneNumber = '';
      let countryCode = '';

      if (formData.phone) {
        try {
          const parsedPhone = parsePhoneNumber(formData.phone);
          if (parsedPhone && parsedPhone.isValid()) {
            countryCode = `+${parsedPhone.countryCallingCode}`;
            phoneNumber = parsedPhone.nationalNumber;
          }
        } catch (error) {
          console.warn('Error parsing phone number:', error);
          // If parsing fails, send the raw phone number
          phoneNumber = formData.phone.trim();
        }
      }

      const userData = {
        email: formData.email.trim(),
        password: formData.password,
        confirm_password: formData.confirmPassword,
        full_name: formData.fullName.trim(),
        phone: phoneNumber,
        country_code: countryCode,
      };

      const result = await register(userData);

      if (result.sessionPreserved) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/verify-email', {
          replace: true,
          state: { email: userData.email },
        });
      }

    } catch (error) {
      console.error('Registration error:', error);
      // Use the error utility for consistent, user-friendly error messages
      const errorMessage = error.userMessage || getErrorMessage(error, 'Registration failed. Please check your information and try again.');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: BRAND.main,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          opacity: 0.3,
        },
      }}
    >
      {/* Back to Home Button */}
      <Box sx={{ position: 'absolute', top: { xs: 16, sm: 24 }, left: { xs: 16, sm: 24 }, zIndex: 10 }}>
        <Button
          onClick={() => navigate('/')}
          startIcon={<ArrowBackIcon />}
          sx={{
            color: 'white',
            fontWeight: 600,
            fontSize: { xs: '0.875rem', sm: '1rem' },
            px: { xs: 2, sm: 3 },
            py: { xs: 1, sm: 1.2 },
            borderRadius: '12px',
            textTransform: 'none',
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.25)',
              transform: 'translateX(-3px)',
            },
          }}
        >
          Back to Home
        </Button>
      </Box>

      <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', alignItems: 'center', pt: { xs: 10, sm: 8 }, pb: { xs: 4, sm: 8 }, px: { xs: 2, sm: 3 }, position: 'relative', zIndex: 1 }}>
        <div style={{ width: '100%' }}>
          <Card
            sx={{
              borderRadius: '24px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              mt: { xs: 6, sm: 0 },
            }}
          >
            <Box sx={{ p: { xs: 4, sm: 6 } }}>
              {/* Logo and Brand */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <div>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 2,
                      background: SUCCESS.main,
                      boxShadow: '0 8px 24px rgba(16, 185, 129, 0.4)',
                    }}
                  >
                    <RocketLaunchIcon sx={{ fontSize: 40 }} />
                  </Avatar>
                </div>

                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    color: NEUTRAL.text,
                    mb: 1,
                    fontSize: { xs: '1.75rem', sm: '2rem' },
                  }}
                >
                  Create Your Account
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: NEUTRAL.textSecondary,
                    fontSize: '1rem',
                  }}
                >
                  Join thousands of restaurants using merchQRy
                </Typography>
              </Box>

              {error && (
                <div>
                  <Alert
                    severity="error"
                    sx={{
                      mb: 3,
                      borderRadius: '12px',
                      '& .MuiAlert-icon': {
                        fontSize: 24,
                      },
                    }}
                  >
                    {error}
                  </Alert>
                </div>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <StyledTextField
                  margin="normal"
                  required
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  autoFocus
                  autoComplete="email"
                  disabled={loading}
                  sx={{ mb: 2 }}
                />

                <StyledTextField
                  margin="normal"
                  required
                  fullWidth
                  label="Full Name"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  autoComplete="name"
                  disabled={loading}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ mb: 2 }}>
                  <PhoneNumberInput
                    value={formData.phone}
                    onChange={(value) => setFormData(prev => ({ ...prev, phone: value || '' }))}
                    label="Phone Number"
                    error={false}
                    disabled={loading}
                  />
                </Box>

                <StyledTextField
                  margin="normal"
                  required
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  helperText="Must be at least 8 characters long"
                  disabled={loading}
                  sx={{ mb: 2 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={togglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <StyledTextField
                  margin="normal"
                  required
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  autoComplete="new-password"
                  disabled={loading}
                  sx={{ mb: 3 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={toggleConfirmPasswordVisibility}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? null : <RocketLaunchIcon />}
                  sx={{
                    background: SUCCESS.main,
                    color: 'white',
                    py: 1.8,
                    borderRadius: '12px',
                    fontSize: '1.05rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    boxShadow: '0 8px 24px rgba(16, 185, 129, 0.4)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: SUCCESS.dark,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 32px rgba(16, 185, 129, 0.5)',
                    },
                    '&:disabled': {
                      background: SUCCESS.light,
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: 'white' }} />
                  ) : (
                    'Create Account'
                  )}
                </Button>

                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <CheckIcon sx={{ fontSize: 'inherit', verticalAlign: '-0.125em', mr: 0.25 }} />
                    No credit card required •{' '}
                    <CheckIcon sx={{ fontSize: 'inherit', verticalAlign: '-0.125em', mr: 0.25 }} />
                    14-day free trial
                  </Typography>
                </Box>

                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    OR
                  </Typography>
                </Divider>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Already have an account?
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/login"
                    variant="outlined"
                    fullWidth
                    sx={{
                      borderColor: BRAND.main,
                      borderWidth: 2,
                      color: BRAND.main,
                      py: 1.5,
                      borderRadius: '12px',
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: BRAND.main,
                        color: 'white',
                        borderWidth: 2,
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                      },
                    }}
                  >
                    Sign In
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                textAlign: 'center',
                py: 3,
                px: 4,
                backgroundColor: '#f9fafb',
                borderTop: '1px solid rgba(0, 0, 0, 0.08)',
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                © {new Date().getFullYear()} merchQRy. All rights reserved.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Link href="/terms" variant="body2" sx={{ color: NEUTRAL.textSecondary, textDecoration: 'none', '&:hover': { color: BRAND.main } }}>
                  Terms
                </Link>
                <Link href="/privacy" variant="body2" sx={{ color: NEUTRAL.textSecondary, textDecoration: 'none', '&:hover': { color: BRAND.main } }}>
                  Privacy
                </Link>
                <Link href="/help" variant="body2" sx={{ color: NEUTRAL.textSecondary, textDecoration: 'none', '&:hover': { color: BRAND.main } }}>
                  Help
                </Link>
              </Box>
            </Box>
          </Card>
        </div>
      </Container>
    </Box>
  );
};

export default UserRegisterPage;
